import { Home } from 'lucide-react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@core/utils/cn';
import { AppBackground, OfflineBanner } from '@shared/components';
import { ADMIN_SECTIONS } from '@features/admin/adminNav';
import { TopNav } from './TopNav';

// Small card-style tabs matching the domain/project tiles: glass surface when
// inactive, solid accent card when active, with a gentle lift on hover.
const tabClass = ({ isActive }: { isActive: boolean }): string =>
  cn(
    'inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-semibold',
    'transition-[transform,background-color,color,border-color] duration-200 active:scale-[0.97]',
    isActive
      ? 'border border-accent bg-accent text-accent-ink shadow-[0_8px_20px_-12px_rgba(62,166,255,0.6)]'
      : 'dda-glass text-muted hover:-translate-y-0.5 hover:text-text',
  );

// Home first, then the sections — same list the home page renders as cards.
const NAV = [
  { to: '/admin/home', labelKey: 'admin.home', Icon: Home },
  ...ADMIN_SECTIONS.map(({ to, labelKey, Icon }) => ({ to, labelKey, Icon })),
];

/** Admin console shell: top nav + Home and every section as small card tabs.
 *  Sections come from the shared ADMIN_SECTIONS list. */
export function AdminLayout(): React.JSX.Element {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  // On the home page the cards already are the menu, so the tab bar would just
  // duplicate them — show the tabs only on the inner section pages.
  const showTabs = pathname !== '/admin/home';
  return (
    <div className="flex min-h-screen flex-col">
      <AppBackground />
      <OfflineBanner />
      <TopNav />
      {showTabs && (
        <div className="border-b border-line">
          <nav
            className="mx-auto flex max-w-4xl flex-wrap gap-2.5 px-4 py-3 sm:px-6"
            aria-label={t('nav.admin')}
          >
            {NAV.map(({ to, labelKey, Icon }) => (
              <NavLink key={to} to={to} className={tabClass}>
                <Icon size={16} aria-hidden="true" />
                {t(labelKey)}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
      <main className="mx-auto w-full max-w-4xl flex-1 px-4 py-8 sm:px-6 sm:py-12">
        <Outlet />
      </main>
    </div>
  );
}
